import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import Container from 'react-bootstrap/Container';
import activeAccessApi from '../api/activeAccessApi';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';
import './AdminActiveAccessBranding.css';

const messageFor = (error, fallback) => {
  const data = error && error.response && error.response.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') return data.detail || data.title || fallback;
  return (error && error.message) || fallback;
};

const AdminActiveAccessBranding = () => {
  const navigate = useNavigate();
  const msgs = useRef(null);
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({ deniedRedirectTo: '/customers' });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        clearAccessIssue();
        setLoading(true);
        setCustomers(await activeAccessApi.getActiveAccessCustomers());
      } catch (error) {
        if (!handleAccessError(error) && msgs.current) msgs.current.show({ severity: 'error', summary: '', detail: messageFor(error, 'Unable to load Active Access customers.'), sticky: true });
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clearAccessIssue, handleAccessError]);

  if (accessIssue) return <AccessState {...accessIssue} />;

  const customerList = Array.isArray(customers) ? customers : [];
  const customerTemplate = (row) => <div className="active-access-customer"><span className="active-access-customer__name">{row.customerName || 'Unnamed customer'}</span><span className="active-access-customer__id">Customer #{row.customerId}</span></div>;
  const tenantTemplate = (row) => <code className="active-access-tenant-path">{row.tenantApiPath || 'Not configured'}</code>;
  const statusTemplate = (row) => <Tag value={row.portalEnabled ? 'Enabled' : 'Disabled'} severity={row.portalEnabled ? 'success' : 'warning'} />;
  const clubCodeTemplate = (row) => row.clubCode ? <code className="active-access-club-code">{row.clubCode}</code> : <span className="active-access-empty">No code</span>;
  const usersTemplate = (row) => <span className="active-access-user-count"><i className="pi pi-users" aria-hidden="true" />{Array.isArray(row.users) ? row.users.length : 0}</span>;
  const actionTemplate = (row) => <Button label="Manage" icon="pi pi-arrow-right" iconPos="right" className="p-button-sm" onClick={() => navigate(`/admin/active-access-branding/${row.customerId}`)} />;

  return <main className="active-access-page"><Container fluid="md"><Card className="active-access-card"><div className="active-access-page__header"><div><p className="active-access-page__eyebrow">Admin catalog</p><h1>Active Access Branding Portal</h1><p className="active-access-page__subtitle">Manage portal access, club codes, permitted users, and tenant branding.</p></div><div className="active-access-page__count"><span>{customerList.length}</span> customer{customerList.length === 1 ? '' : 's'}</div></div><Messages ref={msgs} />{loading ? <div className="active-access-loading"><ProgressBar mode="indeterminate" style={{ height: '6px' }} /></div> : <DataTable value={customerList} className="active-access-table" emptyMessage="No Active Access customers found." responsiveLayout="scroll" stripedRows><Column header="Customer" body={customerTemplate} /><Column header="Tenant API path" body={tenantTemplate} /><Column header="Portal status" body={statusTemplate} /><Column header="Club code" body={clubCodeTemplate} /><Column header="Allowed users" body={usersTemplate} /><Column headerStyle={{ width: '1%', whiteSpace: 'nowrap' }} body={actionTemplate} /></DataTable>}</Card></Container></main>;
};

export default AdminActiveAccessBranding;